import { useCallback, useContext, useMemo } from 'react'
import { Cell, Column, SortingRule } from 'react-table'

import { Table } from 'components/Table'
import { MappedDomainUser } from '../../types'
import DeleteButton from './DeleteButton'
import PermissionCheckbox from './PermissionCheckbox'
import { TableHeader } from 'components/Table/types'
import { TeamPermissionsBlock } from './TeamPermissionsBlock'
import { useAppSelector } from 'app/hooks'
import { domainNestingEnabledSelector } from 'features/auth/permissions/selectors'
import { CurrentUserPermissionsContext } from 'features/auth/permissions/CurrentUserPermissionsContext'
import { UPDATE_PERMISSIONS_ACTIONS } from '../../consts'
import AvatarBlock from './AvatarBlock'
import {
  TeamTableOuterWrapper,
  TeamTableInnerWrapper,
  AvatarCellStyles,
  NameCellStyles,
  CheckboxCellStyles,
} from './styles'

interface TeamTableProps {
  data: MappedDomainUser[]
  searchValue?: string
  headers: Record<string, TableHeader>
  defaultSortBy: SortingRule<MappedDomainUser>
  sortBy: SortingRule<MappedDomainUser>
  setSortBy: (sortBy: SortingRule<MappedDomainUser>) => void
}

function TeamTable({
  data,
  searchValue,
  headers,
  defaultSortBy,
  sortBy,
  setSortBy,
}: TeamTableProps) {
  const { hasDomainAdminPermission } = useContext(CurrentUserPermissionsContext)

  const hasDomainNesting = useAppSelector(domainNestingEnabledSelector)

  const hiddenColumns = useMemo(() => {
    const draftHiddenColumns = []
    if (!hasDomainNesting) {
      draftHiddenColumns.push(headers.config.id)
    }
    if (!hasDomainAdminPermission) {
      draftHiddenColumns.push(headers.userId.id)
    }

    return draftHiddenColumns
  }, [
    headers.config.id,
    headers.userId.id,
    hasDomainAdminPermission,
    hasDomainNesting,
  ])

  const NameCell = ({ value }: Cell) => <strong>{value}</strong>
  const AvatarCell = ({ value }: Cell) => <AvatarBlock url={value} />
  const DeleteButtonCell = (cell: Cell<MappedDomainUser>) => (
    <DeleteButton id={cell.row.original?.id} email={cell.row.original?.email} />
  )

  const getTooltipText = useCallback(
    (updatePermissionsAction: UPDATE_PERMISSIONS_ACTIONS) => {
      return hasDomainAdminPermission &&
        updatePermissionsAction ===
          UPDATE_PERMISSIONS_ACTIONS.UPDATE_SUBSCRIPTION_MANAGEMENT
        ? 'Subscription Management permissions can only be changed in the Savant Community.'
        : ''
    },
    [hasDomainAdminPermission]
  )

  const PermissionCheckboxByAction = useCallback(
    (updatePermissionsAction: UPDATE_PERMISSIONS_ACTIONS) =>
      function PermissionCheckboxCell(cell: Cell<MappedDomainUser>) {
        const original = cell.row.original

        return (
          <PermissionCheckbox
            tooltipText={getTooltipText(updatePermissionsAction)}
            updatePermissionsAction={updatePermissionsAction}
            id={original?.id}
            isAdmin={original?.isAdmin}
            isConfigAuthor={original?.isConfigAuthor}
            isSubscriptionManager={original?.isSubscriptionManager || false}
            disabled={!hasDomainAdminPermission}
          />
        )
      },
    [hasDomainAdminPermission, getTooltipText]
  )

  const columns: readonly Column<MappedDomainUser>[] = useMemo(
    () => [
      {
        Header: '',
        id: headers.avatar.id,
        accessor: (row) => row.avatar,
        Cell: AvatarCell,
        disableSortBy: true,
        disableGlobalFilter: true,
        percentWidth: '5%',
        cellStyles: AvatarCellStyles,
      },
      {
        Header: headers.name.label,
        id: headers.name.id,
        accessor: (row) => row.name,
        Cell: NameCell,
        percentWidth: '20%',
        cellStyles: NameCellStyles,
      },
      {
        Header: headers.email.label,
        id: headers.email.id,
        accessor: (row) => row.email,
        percentWidth: '25%',
      },
      {
        Header: headers.admin.label,
        id: headers.admin.id,
        accessor: (row) => row.isAdmin,
        Cell: PermissionCheckboxByAction(
          UPDATE_PERMISSIONS_ACTIONS.UPDATE_ADMIN
        ),
        disableGlobalFilter: true,
        disableSortBy: true,
        percentWidth: '8%',
        cellStyles: CheckboxCellStyles,
      },
      {
        Header: headers.config.label,
        id: headers.config.id,
        accessor: (row) => row.isConfigAuthor,
        Cell: PermissionCheckboxByAction(
          UPDATE_PERMISSIONS_ACTIONS.UPDATE_CONFIG
        ),
        disableGlobalFilter: true,
        disableSortBy: true,
        percentWidth: '8%',
        cellStyles: CheckboxCellStyles,
      },
      {
        Header: headers.subscriptionManagement.label,
        id: headers.subscriptionManagement.id,
        accessor: (row) => row.isSubscriptionManager,
        Cell: PermissionCheckboxByAction(
          UPDATE_PERMISSIONS_ACTIONS.UPDATE_SUBSCRIPTION_MANAGEMENT
        ),
        disableGlobalFilter: true,
        disableSortBy: true,
        percentWidth: '8%',
        cellStyles: CheckboxCellStyles,
      },
      {
        Header: '',
        id: headers.userId.id,
        accessor: (row) => row.id,
        Cell: DeleteButtonCell,
        disableSortBy: true,
        disableGlobalFilter: true,
        percentWidth: '6%',
      },
    ],
    [headers, PermissionCheckboxByAction]
  )

  return (
    <TeamTableOuterWrapper>
      <TeamTableInnerWrapper>
        <Table
          columns={columns}
          data={data}
          searchValue={searchValue}
          defaultSortBy={defaultSortBy}
          sortBy={sortBy}
          setSortBy={setSortBy}
          hiddenColumns={hiddenColumns}
          mobileViewComponent={TeamPermissionsBlock}
        />
      </TeamTableInnerWrapper>
    </TeamTableOuterWrapper>
  )
}

export default TeamTable
